import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Search } from "components/DesignSystem/Search/Search";
import { IncorporationFormSource } from "constants/incorporation";
import { useToast } from "hooks/useToast";
import { useDebounce } from "utils/debounce";
import { useGetSavedStagesListQuery } from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { useState } from "react";
import { useCreateFormTreeNodeMutation } from "./useFormTreeNodeMutations";
import { buildStageCreatePayloadFromSaved } from "./buildStageCreatePayloadFromSaved";
import Loader from "components/DesignSystem/Loader/loader";

const NEW_STAGE_DEFAULT_TITLE = "Untitled stage";

export const IncorporationAddStageControl = ({
  source,
  parentId,
  onStageCreated,
}: {
  source: IncorporationFormSource;
  parentId: string;
  onStageCreated: (stageId: string) => void;
}) => {
  const { alertToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchTerm = useDebounce(searchValue);

  const { data: savedStages = [], isFetching } = useGetSavedStagesListQuery(
    { search: searchTerm },
    { skip: !isOpen }
  );

  const [createStageNode, { isLoading: isAddingStage }] =
    useCreateFormTreeNodeMutation({ source });
  const [cloneStageFromSaved, { isLoading: isCloning }] =
    useCreateFormTreeNodeMutation({ source });

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const onAddNewStage = async () => {
    try {
      const newStage = await createStageNode({
        treeId: parentId,
        body: {
          stage: { parent_id: parentId, title: NEW_STAGE_DEFAULT_TITLE },
        },
      }).unwrap();
      setIsOpen(false);
      onStageCreated(newStage.uuid);
    } catch (err) {
      onActionError(err);
    }
  };

  const onPickSavedStage = async (savedStage: (typeof savedStages)[number]) => {
    try {
      const newStage = await cloneStageFromSaved({
        treeId: parentId,
        body: {
          stage: buildStageCreatePayloadFromSaved(savedStage, parentId),
        },
      }).unwrap();
      setIsOpen(false);
      onStageCreated(newStage.uuid);
    } catch (err) {
      onActionError(err);
    }
  };

  const isBusy = isAddingStage || isCloning;

  return (
    <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dropdown.Trigger asChild>
        <Button
          type="button"
          customType="secondary"
          block
          className="!t-border-dashed sm:!t-h-auto sm:!t-text-subtext sm:t-w-44 sm:t-shrink-0 sm:t-self-stretch"
        >
          + Add stage
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align="start"
          sideOffset={6}
          onFocusOutside={(e) => e.preventDefault()}
          className="t-flex t-w-60 t-flex-col t-gap-2 t-p-2"
        >
          <Search
            block
            value={searchValue}
            onChange={onSearchChange}
            placeholder="Search saved stages"
          />
          <div className="t-flex t-max-h-48 t-flex-col t-gap-1 t-overflow-y-auto">
            {isFetching ? (
              <div className="t-p-4">
                <Loader size="small" />
              </div>
            ) : (
              savedStages.map((savedStage) => (
                <Dropdown.Item
                  key={savedStage.uuid}
                  disabled={isBusy}
                  onSelect={() => onPickSavedStage(savedStage)}
                >
                  <div className="t-flex t-flex-col">
                    <span className="t-text-body-sm  t-text-text-100">
                      {savedStage.display_name}
                    </span>
                  </div>
                </Dropdown.Item>
              ))
            )}
          </div>
          <div className="t-border-0 t-border-t t-border-solid t-border-neutral-10 t-pt-2">
            <Button
              type="button"
              customType="link"
              block
              disabled={isBusy}
              isLoading={isAddingStage}
              onClick={onAddNewStage}
            >
              Add new stage
            </Button>
          </div>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
